// ============================================================================
// STUDIO CRM — studio bookings with their Dynamics 365 account and opportunity
// links. Tabs focus the table on past sessions that still need linking; the
// header shows linking coverage, then pipeline/TCV once there's data. Clicking
// a row opens a side panel with the booking, account and opportunities.
// Tier 1 + Tier 2 facilitators + system admins.
// ============================================================================

import React, { useCallback, useEffect, useMemo, useState } from 'react';
import {
  TrendingUp, Loader, AlertCircle, X, Search, Download, ChevronLeft, ChevronRight, MessageSquare,
  ArrowUp, ArrowDown, ChevronsUpDown, Info, SlidersHorizontal, Layers, Coins
} from 'lucide-react';
import { useAuth } from '../contexts/AuthContext';
import {
  listAllBookings,
  listSectors,
  STATUS_LABELS,
  STATUS_COLORS,
  BookingStatus,
  StudioBooking,
  Sector
} from '../services/studio-bookings';
import { listAllRoomsWithStatus, RoomWithAvailability } from '../services/rooms';
import {
  CrmBooking,
  BookingAccountLink,
  BookingOpportunityLink,
  BookingCrmExclusion,
  CrmNotesSummary,
  CrmAttribution,
  OpportunityState,
  STATE_LABELS,
  ATTRIBUTION_LABELS,
  EXCLUSION_REASON_LABELS,
  CrmFxRates,
  getFxRates,
  listCrmBookings,
  effectiveValues
} from '../services/studio-crm';
import {
  computeTotals,
  findConflicts,
  bookingFigures,
  formatCurrencyTotals
} from '../components/studio-crm/crm-totals';
import MoneyCard, { CARD, CARD_LABEL } from '../components/studio-crm/MoneyCard';
import FxRatesDialog from '../components/studio-crm/FxRatesDialog';
import OpportunityDialog from '../components/studio-crm/OpportunityDialog';
import CrmDrawer, { BILLING_LABELS, formatDate, customerLabel } from '../components/studio-crm/CrmDrawer';
import { BookingModal } from '../components/studio-calendar/BookingModal';
import { toCsv, downloadCsv, CsvCell } from '../utils/csv';

// ============================================================================
// TYPES + HELPERS
// ============================================================================

type Tab = 'needs' | 'linked' | 'upcoming' | 'excluded' | 'all';
type Period = 'all' | 'this_quarter' | 'last_quarter' | 'last_12m' | 'this_year' | 'custom';
type SortCol = 'date' | 'customer' | 'opps';
type SortDir = 'asc' | 'desc';

interface Filters {
  search: string;
  period: Period;
  from: string;
  to: string;
  sector: string;
  facilitator: string;
  eventType: string;
  status: string;
  billing: string;
  oppState: string;
  attribution: string;
}

const EMPTY_FILTERS: Filters = {
  search: '', period: 'all', from: '', to: '', sector: '', facilitator: '', eventType: '',
  status: '', billing: '', oppState: '', attribution: ''
};
const MORE_FILTER_KEYS: (keyof Filters)[] = ['sector', 'facilitator', 'eventType', 'status', 'billing', 'oppState', 'attribution'];

const TABS: { key: Tab; label: string; hint: string }[] = [
  { key: 'needs', label: 'Needs linking', hint: 'Past approved sessions with no opportunities' },
  { key: 'linked', label: 'Linked', hint: 'Bookings with at least one opportunity' },
  { key: 'upcoming', label: 'Upcoming', hint: 'Pending or approved sessions from today' },
  { key: 'excluded', label: 'No CRM needed', hint: 'Bookings marked as not needing a CRM record' },
  { key: 'all', label: 'All', hint: 'Every booking, any status' }
];

const PERIOD_LABELS: Record<Period, string> = {
  all: 'All time', this_quarter: 'This quarter', last_quarter: 'Last quarter',
  last_12m: 'Last 12 months', this_year: 'This year', custom: 'Custom dates'
};

const PAGE_SIZES = [25, 50, 100];

// Fields the CRM summary shares with a full booking, refreshed after an edit in BookingModal
const SUMMARY_KEYS = [
  'roomName', 'customer', 'sector', 'eventTypeName', 'facilitatorName', 'facilitatorTier', 'billingType',
  'customerStatus', 'dateType', 'startDate', 'endDate', 'status'
] as const;

function ymd(d: Date): string {
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
}

function periodRange(f: Filters, today: Date): { from: string; to: string } {
  const y = today.getFullYear();
  const q = Math.floor(today.getMonth() / 3);
  switch (f.period) {
    case 'this_quarter': return { from: ymd(new Date(y, q * 3, 1)), to: ymd(new Date(y, q * 3 + 3, 0)) };
    case 'last_quarter': return { from: ymd(new Date(y, q * 3 - 3, 1)), to: ymd(new Date(y, q * 3, 0)) };
    case 'last_12m': return { from: ymd(new Date(y - 1, today.getMonth(), today.getDate() + 1)), to: ymd(today) };
    case 'this_year': return { from: `${y}-01-01`, to: `${y}-12-31` };
    case 'custom': return { from: f.from, to: f.to };
    default: return { from: '', to: '' };
  }
}

function matchesOppFilters(link: BookingOpportunityLink, f: Filters): boolean {
  if (f.oppState && effectiveValues(link).state !== f.oppState) return false;
  if (f.attribution && link.attribution !== f.attribution) return false;
  return true;
}

function isPast(b: CrmBooking, today: string): boolean {
  return (b.endDate || b.startDate) < today;
}

function inTab(b: CrmBooking, tab: Tab, today: string): boolean {
  switch (tab) {
    case 'needs': return b.status === 'approved' && isPast(b, today) && b.crm.opportunities.length === 0 && !b.crm.exclusion;
    case 'excluded': return !!b.crm.exclusion;
    case 'linked': return b.crm.opportunities.length > 0;
    case 'upcoming': return !isPast(b, today) && (b.status === 'approved' || b.status === 'pending');
    default: return true;
  }
}

function accountKey(b: CrmBooking): string {
  return customerLabel(b).trim().toLowerCase();
}

function uniqueSorted(values: (string | undefined)[]): string[] {
  return Array.from(new Set(values.filter((v): v is string => !!v))).sort((a, b) => a.localeCompare(b));
}

function exportCsv(bookings: CrmBooking[], filters: Filters) {
  const header = [
    'Booking date', 'Customer', 'Dynamics account', 'Sector', 'Event type', 'Facilitator', 'Booking status',
    'Billing', 'Opportunity ID', 'Opportunity', 'Attribution', 'State', 'Stage', 'Value', 'Currency',
    'Close date', 'Values source', 'Linked by', 'Linked at', 'No CRM needed'
  ];
  const rows: CsvCell[][] = [];
  for (const b of bookings) {
    const base = [b.startDate, b.customer, b.crm.account?.accountName, b.sector, b.eventTypeName,
      b.facilitatorName, b.status, BILLING_LABELS[b.billingType] || b.billingType];
    const excluded = b.crm.exclusion
      ? [EXCLUSION_REASON_LABELS[b.crm.exclusion.reason], b.crm.exclusion.note].filter(Boolean).join(': ')
      : '';
    const opps = b.crm.opportunities.filter(o => matchesOppFilters(o, filters));
    if (opps.length === 0) {
      rows.push([...base, '', '', '', '', '', '', '', '', '', '', '', excluded]);
      continue;
    }
    for (const o of opps) {
      const v = effectiveValues(o);
      rows.push([...base, o.opportunityId, v.name, ATTRIBUTION_LABELS[o.attribution], STATE_LABELS[v.state],
        v.stage, v.value, v.currency, v.closeDate, o.syncStatus === 'ok' && o.current ? 'Dynamics' : 'Manual',
        o.linkedByName, o.linkedAt, excluded]);
    }
  }
  downloadCsv(toCsv(header, rows), `studio-crm-${new Date().toISOString().slice(0, 10)}.csv`);
}

// Mutations patch local state from the API response instead of re-reading the
// list: GSI4 is eventually consistent, so an immediate reload can miss the write.
type BookingPatch = (booking: CrmBooking) => CrmBooking;

function withAccount(account: BookingAccountLink | null): BookingPatch {
  return b => ({ ...b, crm: { ...b.crm, account } });
}

function withOpportunity(link: BookingOpportunityLink): BookingPatch {
  return b => {
    const others = b.crm.opportunities.filter(o => o.opportunityId !== link.opportunityId);
    const opportunities = [...others, link].sort((x, y) => x.linkedAt.localeCompare(y.linkedAt));
    return { ...b, crm: { ...b.crm, opportunities } };
  };
}

function withoutOpportunity(opportunityId: string): BookingPatch {
  return b => ({ ...b, crm: { ...b.crm, opportunities: b.crm.opportunities.filter(o => o.opportunityId !== opportunityId) } });
}

function withExclusion(exclusion: BookingCrmExclusion | null): BookingPatch {
  return b => ({ ...b, crm: { ...b.crm, exclusion } });
}

function withNotesSummary(summary: CrmNotesSummary): BookingPatch {
  return b => ({ ...b, crm: { ...b.crm, notes: summary } });
}

function withBookingFields(updated: StudioBooking): BookingPatch {
  return b => {
    const next: any = { ...b };
    for (const key of SUMMARY_KEYS) next[key] = updated[key];
    return next as CrmBooking;
  };
}

// ============================================================================
// SMALL COMPONENTS
// ============================================================================

const TH = 'px-4 py-3 text-left text-xs font-semibold text-slate-500 uppercase tracking-wider';

function ColHeader({ col, label, sort, onSort, className = '' }: {
  col: SortCol; label: string; sort: { col: SortCol; dir: SortDir }; onSort: (c: SortCol) => void; className?: string;
}) {
  const active = sort.col === col;
  const Icon = !active ? ChevronsUpDown : sort.dir === 'asc' ? ArrowUp : ArrowDown;
  return (
    <th className={`${TH} ${className}`}>
      <button onClick={() => onSort(col)} className="flex items-center gap-1 uppercase tracking-wider hover:text-navy transition-colors">
        {label} <Icon className={`w-3 h-3 ${active ? 'text-primary' : 'text-slate-300'}`} />
      </button>
    </th>
  );
}

function BookingRow({ booking, filters, onOpen }: { booking: CrmBooking; filters: Filters; onOpen: () => void }) {
  const allOpps = booking.crm.opportunities;
  const opps = allOpps.filter(o => matchesOppFilters(o, filters));
  const hiddenCount = allOpps.length - opps.length;
  const figures = bookingFigures(opps);
  const subtitle = [booking.eventTypeName, booking.sector].filter(Boolean).join(' · ');

  return (
    <tr className="hover:bg-primary/[0.03] cursor-pointer" onClick={onOpen}>
      <td className="px-4 py-2.5 whitespace-nowrap text-slate-600">{formatDate(booking.startDate)}</td>
      <td className="px-4 py-2.5 max-w-[320px]">
        <div className="flex items-center gap-2 min-w-0">
          <span className="font-semibold text-navy truncate">{customerLabel(booking)}</span>
          {!!booking.crm.notes?.count && (
            <span className="inline-flex items-center gap-0.5 text-[11px] font-semibold text-slate-500 whitespace-nowrap"
              title={`${booking.crm.notes.count} ${booking.crm.notes.count === 1 ? 'note' : 'notes'}`
                + (booking.crm.notes.lastNoteAt ? `, last on ${formatDate(booking.crm.notes.lastNoteAt.slice(0, 10))}` : '')}>
              <MessageSquare size={12} className="text-slate-400" /> {booking.crm.notes.count}
            </span>
          )}
          {booking.crm.exclusion && (
            <span className="text-[10px] font-semibold px-1.5 rounded bg-slate-100 text-slate-500 whitespace-nowrap">
              NO CRM · {EXCLUSION_REASON_LABELS[booking.crm.exclusion.reason].toUpperCase()}
            </span>
          )}
        </div>
        <div className="text-xs text-slate-400 truncate">{subtitle}</div>
      </td>
      <td className="px-4 py-2.5 text-center text-slate-600" title={hiddenCount ? `${hiddenCount} hidden by filters` : undefined}>
        {allOpps.length === 0 ? '—' : hiddenCount ? `${opps.length} of ${allOpps.length}` : opps.length}
      </td>
      <td className="px-4 py-2.5 whitespace-nowrap text-slate-700">{formatCurrencyTotals(figures.pipeline)}</td>
      <td className="px-4 py-2.5 whitespace-nowrap font-semibold text-navy">{formatCurrencyTotals(figures.won)}</td>
      <td className="px-4 py-2.5 whitespace-nowrap text-slate-600 max-w-[160px] truncate">{booking.facilitatorName}</td>
      <td className="px-4 py-2.5">
        <span className={`text-xs font-semibold px-2 py-0.5 rounded-full border whitespace-nowrap ${STATUS_COLORS[booking.status]}`}>
          {STATUS_LABELS[booking.status]}
        </span>
      </td>
      <td className="px-4 py-2.5 text-slate-500 whitespace-nowrap">{BILLING_LABELS[booking.billingType] || booking.billingType}</td>
    </tr>
  );
}

function GroupRow({ name, bookings, filters }: { name: string; bookings: CrmBooking[]; filters: Filters }) {
  const totals = computeTotals(bookings.flatMap(b => b.crm.opportunities.filter(o => matchesOppFilters(o, filters))));
  return (
    <tr className="bg-slate-50 border-t-2 border-[#e2e8f0]">
      <td className="px-4 py-2 text-xs text-slate-500 whitespace-nowrap">{bookings.length} {bookings.length === 1 ? 'session' : 'sessions'}</td>
      <td className="px-4 py-2 font-bold text-navy">{name}</td>
      <td className="px-4 py-2 text-center text-xs font-semibold text-slate-600">{totals.opportunityCount || '—'}</td>
      <td className="px-4 py-2 whitespace-nowrap text-xs font-semibold text-slate-700">{formatCurrencyTotals(totals.openPipeline)}</td>
      <td className="px-4 py-2 whitespace-nowrap text-xs font-bold text-navy">{formatCurrencyTotals(totals.wonTcv)}</td>
      <td colSpan={3} />
    </tr>
  );
}

// ============================================================================
// PAGE
// ============================================================================

export default function StudioCrm() {
  const [bookings, setBookings] = useState<CrmBooking[]>([]);
  const [fullBookings, setFullBookings] = useState<Map<string, StudioBooking>>(new Map());
  const [rooms, setRooms] = useState<RoomWithAvailability[]>([]);
  const [sectors, setSectors] = useState<Sector[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const [tab, setTab] = useState<Tab>('needs');
  const [filters, setFilters] = useState<Filters>(EMPTY_FILTERS);
  const [showMore, setShowMore] = useState(false);
  const [groupByAccount, setGroupByAccount] = useState(false);
  const [sort, setSort] = useState<{ col: SortCol; dir: SortDir }>({ col: 'date', dir: 'desc' });
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(25);

  const { user, can } = useAuth();
  const canSetFx = can('Studio::ManageFxRates');
  const [fxRates, setFxRates] = useState<CrmFxRates | null>(null);
  const [showFx, setShowFx] = useState(false);

  const [openId, setOpenId] = useState<string | null>(null);
  const [bookingModalOpen, setBookingModalOpen] = useState(false);
  const [dialog, setDialog] = useState<{ bookingId: string; existing?: BookingOpportunityLink } | null>(null);

  const load = useCallback(async () => {
    try {
      setBookings(await listCrmBookings());
      setError(null);
    } catch (err: any) {
      setError(err?.response?.data?.error || 'Failed to load bookings');
    } finally {
      setLoading(false);
    }
    getFxRates().then(setFxRates).catch(() => undefined);
    // Full records + lookups for the booking view/edit modal. Role-scoped by the
    // existing calendar endpoint, so a failure only disables "View / edit booking".
    Promise.all([listAllBookings(), listAllRoomsWithStatus(), listSectors()])
      .then(([full, r, s]) => {
        setFullBookings(new Map(full.map(b => [b.bookingId, b])));
        setRooms(r);
        setSectors(s);
      })
      .catch(() => undefined);
  }, []);

  useEffect(() => { load(); }, [load]);

  const patchBooking = useCallback((bookingId: string, patch: BookingPatch) => {
    setBookings(prev => prev.map(b => (b.bookingId === bookingId ? patch(b) : b)));
  }, []);

  // Stable so the notes timeline doesn't refetch on every page render
  const notesSummary = useCallback((summary: CrmNotesSummary) => {
    if (openId) patchBooking(openId, withNotesSummary(summary));
  }, [openId, patchBooking]);

  const today = useMemo(() => new Date(), []);
  const todayYmd = ymd(today);

  const options = useMemo(() => ({
    sectors: uniqueSorted(bookings.map(b => b.sector)),
    facilitators: uniqueSorted(bookings.map(b => b.facilitatorName)),
    eventTypes: uniqueSorted(bookings.map(b => b.eventTypeName)),
    accountNames: uniqueSorted(bookings.map(b => b.crm.account?.accountName))
  }), [bookings]);

  // Period + filters (not the tab) — drives the header figures
  const inScope = useMemo(() => {
    const f = filters;
    const { from, to } = periodRange(f, today);
    const search = f.search.trim().toLowerCase();
    const oppFilterActive = !!(f.oppState || f.attribution);
    return bookings.filter(b => {
      if (from && b.startDate < from) return false;
      if (to && b.startDate > to) return false;
      if (search && !`${b.customer} ${b.crm.account?.accountName || ''}`.toLowerCase().includes(search)) return false;
      if (f.sector && b.sector !== f.sector) return false;
      if (f.facilitator && b.facilitatorName !== f.facilitator) return false;
      if (f.eventType && b.eventTypeName !== f.eventType) return false;
      if (f.status && b.status !== f.status) return false;
      if (f.billing && b.billingType !== f.billing) return false;
      if (oppFilterActive && !b.crm.opportunities.some(o => matchesOppFilters(o, f))) return false;
      return true;
    });
  }, [bookings, filters, today]);

  const tabCounts = useMemo(() => {
    const counts = { needs: 0, linked: 0, upcoming: 0, excluded: 0, all: inScope.length } as Record<Tab, number>;
    for (const b of inScope) {
      if (inTab(b, 'excluded', todayYmd)) counts.excluded++;
      if (inTab(b, 'needs', todayYmd)) counts.needs++;
      if (inTab(b, 'linked', todayYmd)) counts.linked++;
      if (inTab(b, 'upcoming', todayYmd)) counts.upcoming++;
    }
    return counts;
  }, [inScope, todayYmd]);

  const coverage = useMemo(() => {
    const past = inScope.filter(b => b.status === 'approved' && isPast(b, todayYmd) && !b.crm.exclusion);
    const linked = past.filter(b => b.crm.opportunities.length > 0).length;
    return { past: past.length, linked, pct: past.length ? Math.round((linked / past.length) * 100) : 0 };
  }, [inScope, todayYmd]);

  const totals = useMemo(
    () => computeTotals(inScope.flatMap(b => b.crm.opportunities.filter(o => matchesOppFilters(o, filters)))),
    [inScope, filters]
  );
  const conflictingIds = useMemo(() => findConflicts(bookings.flatMap(b => b.crm.opportunities)), [bookings]);

  const visible = useMemo(() => {
    const rows = inScope.filter(b => inTab(b, tab, todayYmd));
    const dir = sort.dir === 'asc' ? 1 : -1;
    rows.sort((a, b) => {
      if (sort.col === 'customer') return dir * customerLabel(a).localeCompare(customerLabel(b));
      if (sort.col === 'opps') return dir * (a.crm.opportunities.length - b.crm.opportunities.length);
      return dir * a.startDate.localeCompare(b.startDate);
    });
    if (!groupByAccount) return rows;
    // Groups ordered by account name; bookings keep the chosen sort within a group
    const groups = new Map<string, CrmBooking[]>();
    for (const b of rows) {
      const k = accountKey(b);
      groups.set(k, [...(groups.get(k) || []), b]);
    }
    return Array.from(groups.values())
      .sort((a, b) => customerLabel(a[0]).localeCompare(customerLabel(b[0])))
      .flat();
  }, [inScope, tab, todayYmd, sort, groupByAccount]);

  const totalPages = Math.max(1, Math.ceil(visible.length / pageSize));
  const safePage = Math.min(page, totalPages);
  const paginated = visible.slice((safePage - 1) * pageSize, safePage * pageSize);

  function setFilter<K extends keyof Filters>(key: K, val: Filters[K]) {
    setFilters(prev => ({ ...prev, [key]: val }));
    setPage(1);
  }

  function changeTab(next: Tab) {
    setTab(next);
    setSort({ col: 'date', dir: next === 'upcoming' ? 'asc' : 'desc' });
    setPage(1);
  }

  function handleSort(col: SortCol) {
    setSort(prev => prev.col === col ? { col, dir: prev.dir === 'asc' ? 'desc' : 'asc' } : { col, dir: col === 'date' ? 'desc' : 'asc' });
    setPage(1);
  }

  const moreCount = MORE_FILTER_KEYS.filter(k => filters[k] !== '').length;
  const hasActiveFilters = JSON.stringify(filters) !== JSON.stringify(EMPTY_FILTERS);
  const inputCls = 'h-9 rounded-lg border border-[#e2e8f0] bg-white px-2.5 text-xs text-navy focus:outline-none focus:ring-2 focus:ring-primary/30 focus:border-primary transition-colors';
  // A percentage from one or two closed deals reads as more certain than it is
  const MIN_CLOSED_FOR_WIN_RATE = 5;
  const winRate = totals.winRate !== null && totals.wonCount + totals.lostCount >= MIN_CLOSED_FOR_WIN_RATE
    ? `${Math.round(totals.winRate * 100)}%` : null;
  const openBooking = openId ? bookings.find(b => b.bookingId === openId) || null : null;
  const openFull = openId ? fullBookings.get(openId) || null : null;
  const dialogBooking = dialog ? bookings.find(b => b.bookingId === dialog.bookingId) || null : null;

  return (
    <div className="flex-1 overflow-y-auto p-8 font-display">
      <div className="w-full max-w-[1400px] mx-auto flex flex-col gap-6">

        {/* Header */}
        <header className="flex items-center justify-between border-b border-slate-200 pb-6">
          <div className="flex items-center gap-4">
            <div className="flex items-center justify-center w-10 h-10 rounded-full bg-primary/10 text-primary">
              <TrendingUp size={22} />
            </div>
            <div>
              <h1 className="text-2xl font-bold tracking-tight text-navy">Studio CRM</h1>
              <p className="text-slate-500 text-sm font-medium">Dynamics 365 opportunities linked to studio sessions</p>
            </div>
          </div>
          <div className="flex items-center gap-3">
            {canSetFx && (
              <button onClick={() => setShowFx(true)}
                className="flex items-center justify-center gap-2 rounded-xl h-10 px-4 border border-slate-200 bg-white text-slate-600 text-sm font-bold hover:bg-slate-50 shadow-sm transition-colors">
                <Coins size={16} /> Exchange rates
              </button>
            )}
            <button onClick={() => exportCsv(visible, filters)} disabled={visible.length === 0}
              className="flex items-center justify-center gap-2 rounded-xl h-10 px-4 border border-slate-200 bg-white text-slate-600 text-sm font-bold hover:bg-slate-50 shadow-sm transition-colors disabled:opacity-50">
              <Download size={16} /> Export CSV
            </button>
          </div>
        </header>

        {error && (
          <div className="p-4 rounded-xl bg-red-50 border border-red-200 flex items-center gap-3">
            <AlertCircle className="w-5 h-5 text-red-500 flex-shrink-0" />
            <p className="text-sm text-red-700">{error}</p>
          </div>
        )}

        {/* Coverage + figures */}
        <div className="flex flex-wrap items-stretch gap-3">
          <div className={`${CARD} justify-between`}>
            <div className="flex items-center justify-between">
              <span className={CARD_LABEL}>Past sessions linked</span>
              <span title={'Approved sessions that have finished, in the selected period and filters, excluding bookings marked No CRM needed. '
                + 'Money figures count each opportunity once even if linked to several bookings. ≈ EUR totals use fixed reporting rates'
                + (fxRates ? ` set by ${fxRates.updatedByName} on ${formatDate(fxRates.updatedAt.slice(0, 10))}` : ' (not set yet)')
                + '. Attribution is entered by facilitators, so figures show association with sessions, not proof of cause. '
                + 'Tabs only change the table, not these figures.'}>
                <Info size={14} className="text-slate-400 cursor-help" />
              </span>
            </div>
            <div className="flex items-baseline gap-2 mt-1">
              <span className="text-2xl font-bold text-navy">{coverage.linked} of {coverage.past}</span>
              <span className="text-sm font-semibold text-primary">{coverage.pct}%</span>
            </div>
            <div className="h-1.5 rounded-full bg-slate-100 mt-2 overflow-hidden">
              <div className="h-full bg-primary rounded-full transition-all" style={{ width: `${coverage.pct}%` }} />
            </div>
          </div>

          {totals.opportunityCount > 0 ? (
            <>
              <MoneyCard label="Open pipeline" totals={totals.openPipeline} split={totals.pipelineSplit}
                rates={fxRates?.rates || null}
                footer={`${totals.openCount} open ${totals.openCount === 1 ? 'opportunity' : 'opportunities'}`} />
              <MoneyCard label="Won TCV" totals={totals.wonTcv} split={totals.wonSplit}
                rates={fxRates?.rates || null}
                footer={`${totals.wonCount} won · ${totals.lostCount} lost${winRate ? ` · ${winRate} win rate` : ''}`} />
            </>
          ) : (
            <div className="flex flex-[2] items-center rounded-xl border border-dashed border-slate-300 px-4 text-sm text-slate-500">
              Pipeline and won TCV appear here once opportunities are linked. Start with the Needs linking tab.
            </div>
          )}
        </div>

        {/* Tabs + filters + table */}
        <div className="bg-white border border-[#e2e8f0] rounded-2xl overflow-hidden">
          <div className="flex items-center gap-1 px-4 pt-3 border-b border-[#e2e8f0]">
            {TABS.map(t => (
              <button key={t.key} onClick={() => changeTab(t.key)} title={t.hint}
                className={`flex items-center gap-2 px-3 py-2 text-sm font-semibold border-b-2 -mb-px transition-colors ${
                  tab === t.key ? 'border-primary text-navy' : 'border-transparent text-slate-500 hover:text-navy'
                }`}>
                {t.label}
                <span className={`text-xs px-1.5 py-0.5 rounded-full ${tab === t.key ? 'bg-primary/10 text-primary' : 'bg-slate-100 text-slate-500'}`}>
                  {tabCounts[t.key]}
                </span>
              </button>
            ))}
          </div>

          <div className="p-4 border-b border-[#e2e8f0] flex flex-col gap-3">
            <div className="flex flex-wrap gap-3 items-center">
              <div className="relative">
                <Search className="absolute left-2.5 top-1/2 -translate-y-1/2 w-3.5 h-3.5 text-slate-400 pointer-events-none" />
                <input type="text" placeholder="Customer or account" value={filters.search}
                  onChange={e => setFilter('search', e.target.value)} className={`${inputCls} pl-8 w-56`} />
              </div>
              <select value={filters.period} onChange={e => setFilter('period', e.target.value as Period)} className={inputCls}>
                {(Object.keys(PERIOD_LABELS) as Period[]).map(p => <option key={p} value={p}>{PERIOD_LABELS[p]}</option>)}
              </select>
              {filters.period === 'custom' && (
                <>
                  <input type="date" value={filters.from} onChange={e => setFilter('from', e.target.value)} className={inputCls} aria-label="From" />
                  <span className="text-xs text-slate-400">to</span>
                  <input type="date" value={filters.to} onChange={e => setFilter('to', e.target.value)} className={inputCls} aria-label="To" />
                </>
              )}
              <button onClick={() => setShowMore(s => !s)}
                className={`${inputCls} flex items-center gap-1.5 font-semibold ${moreCount || showMore ? 'border-primary text-primary' : ''}`}>
                <SlidersHorizontal size={13} /> More filters{moreCount ? ` (${moreCount})` : ''}
              </button>
              <label className="flex items-center gap-1.5 text-xs text-slate-600 cursor-pointer">
                <input type="checkbox" checked={groupByAccount} onChange={e => { setGroupByAccount(e.target.checked); setPage(1); }}
                  className="rounded border-slate-300 text-primary focus:ring-primary/30" />
                <Layers size={13} className="text-slate-400" /> Group by account
              </label>
              {hasActiveFilters && (
                <button onClick={() => { setFilters(EMPTY_FILTERS); setPage(1); }}
                  className="h-9 px-2.5 text-xs text-slate-500 hover:text-navy flex items-center gap-1 rounded-lg hover:bg-slate-100 transition-colors">
                  <X className="w-3 h-3" /> Clear
                </button>
              )}
              <span className="ml-auto text-xs text-slate-400">
                {visible.length} {visible.length === 1 ? 'booking' : 'bookings'}
              </span>
            </div>

            {showMore && (
              <div className="flex flex-wrap gap-3 items-center pt-3 border-t border-dashed border-[#e2e8f0]">
                <select value={filters.sector} onChange={e => setFilter('sector', e.target.value)} className={inputCls}>
                  <option value="">All sectors</option>
                  {options.sectors.map(s => <option key={s} value={s}>{s}</option>)}
                </select>
                <select value={filters.facilitator} onChange={e => setFilter('facilitator', e.target.value)} className={inputCls}>
                  <option value="">All facilitators</option>
                  {options.facilitators.map(s => <option key={s} value={s}>{s}</option>)}
                </select>
                <select value={filters.eventType} onChange={e => setFilter('eventType', e.target.value)} className={inputCls}>
                  <option value="">All event types</option>
                  {options.eventTypes.map(s => <option key={s} value={s}>{s}</option>)}
                </select>
                <select value={filters.status} onChange={e => setFilter('status', e.target.value)} className={inputCls}>
                  <option value="">All booking statuses</option>
                  {(['pending', 'approved', 'rejected', 'cancelled'] as BookingStatus[]).map(s => (
                    <option key={s} value={s}>{STATUS_LABELS[s]}</option>
                  ))}
                </select>
                <select value={filters.billing} onChange={e => setFilter('billing', e.target.value)} className={inputCls}>
                  <option value="">All billing</option>
                  {Object.entries(BILLING_LABELS).map(([k, v]) => <option key={k} value={k}>{v}</option>)}
                </select>
                <select value={filters.oppState} onChange={e => setFilter('oppState', e.target.value)} className={inputCls}>
                  <option value="">All opp states</option>
                  {(['open', 'won', 'lost'] as OpportunityState[]).map(s => <option key={s} value={s}>{STATE_LABELS[s]}</option>)}
                </select>
                <select value={filters.attribution} onChange={e => setFilter('attribution', e.target.value)} className={inputCls}>
                  <option value="">Sourced + influenced</option>
                  {(['sourced', 'influenced'] as CrmAttribution[]).map(s => <option key={s} value={s}>{ATTRIBUTION_LABELS[s]}</option>)}
                </select>
              </div>
            )}
          </div>

          {loading ? (
            <div className="flex justify-center items-center py-20 text-[#64748b]"><Loader className="w-6 h-6 animate-spin" /></div>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead>
                  <tr className="border-b border-[#e2e8f0] bg-slate-50/50">
                    <ColHeader col="date" label="Date" sort={sort} onSort={handleSort} />
                    <ColHeader col="customer" label="Customer" sort={sort} onSort={handleSort} />
                    <ColHeader col="opps" label="Opps" sort={sort} onSort={handleSort} className="text-center" />
                    <th className={TH}>Pipeline</th>
                    <th className={TH}>Won TCV</th>
                    <th className={TH}>Facilitator</th>
                    <th className={TH}>Status</th>
                    <th className={TH}>Billing</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-[#e2e8f0]">
                  {paginated.length === 0 ? (
                    <tr>
                      <td colSpan={8} className="px-4 py-12 text-center text-sm text-slate-400">
                        {tab === 'needs' && !hasActiveFilters
                          ? 'Every past session has at least one opportunity linked.'
                          : 'No bookings match the current tab and filters.'}
                      </td>
                    </tr>
                  ) : paginated.map((b, i) => {
                    const startsGroup = groupByAccount && (i === 0 || accountKey(paginated[i - 1]) !== accountKey(b));
                    return (
                      <React.Fragment key={b.bookingId}>
                        {startsGroup && (
                          <GroupRow name={customerLabel(b)} filters={filters}
                            bookings={visible.filter(x => accountKey(x) === accountKey(b))} />
                        )}
                        <BookingRow booking={b} filters={filters} onOpen={() => setOpenId(b.bookingId)} />
                      </React.Fragment>
                    );
                  })}
                </tbody>
              </table>
            </div>
          )}
        </div>

        {/* Pagination */}
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-2 text-xs text-slate-500">
            Rows per page:
            {PAGE_SIZES.map(s => (
              <button key={s} onClick={() => { setPageSize(s); setPage(1); }}
                className={`px-2 py-1 rounded-lg transition-colors ${pageSize === s ? 'bg-primary/10 text-primary font-semibold' : 'hover:bg-slate-100'}`}>
                {s}
              </button>
            ))}
          </div>
          <div className="flex items-center gap-1">
            <span className="text-xs text-slate-400 mr-2">
              {visible.length === 0 ? '0' : `${(safePage - 1) * pageSize + 1}–${Math.min(safePage * pageSize, visible.length)}`} of {visible.length}
            </span>
            <button onClick={() => setPage(p => Math.max(1, p - 1))} disabled={safePage === 1}
              className="p-1.5 rounded-lg hover:bg-slate-100 disabled:opacity-30 disabled:cursor-not-allowed transition-colors">
              <ChevronLeft className="w-4 h-4 text-slate-600" />
            </button>
            <span className="text-xs text-slate-500 px-2">{safePage} / {totalPages}</span>
            <button onClick={() => setPage(p => Math.min(totalPages, p + 1))} disabled={safePage === totalPages}
              className="p-1.5 rounded-lg hover:bg-slate-100 disabled:opacity-30 disabled:cursor-not-allowed transition-colors">
              <ChevronRight className="w-4 h-4 text-slate-600" />
            </button>
          </div>
        </div>
      </div>

      {openBooking && !bookingModalOpen && !dialog && (
        <CrmDrawer
          booking={openBooking}
          fullBooking={openFull}
          accountNames={options.accountNames}
          conflictingIds={conflictingIds}
          onClose={() => setOpenId(null)}
          onAccountChanged={account => patchBooking(openBooking.bookingId, withAccount(account))}
          onOpportunityRemoved={id => patchBooking(openBooking.bookingId, withoutOpportunity(id))}
          onAddOpportunity={() => setDialog({ bookingId: openBooking.bookingId })}
          onEditOpportunity={link => setDialog({ bookingId: openBooking.bookingId, existing: link })}
          onOpenBooking={() => setBookingModalOpen(true)}
          onExclusionChanged={exclusion => patchBooking(openBooking.bookingId, withExclusion(exclusion))}
          currentUser={user ? { userId: user.userId, canModerateNotes: can('Studio::ModerateCrmNotes') } : null}
          onNotesSummary={notesSummary}
        />
      )}

      {bookingModalOpen && openFull && (
        <BookingModal
          booking={openFull}
          rooms={rooms}
          sectors={sectors}
          onClose={() => setBookingModalOpen(false)}
          onUpdated={updated => {
            setFullBookings(prev => new Map(prev).set(updated.bookingId, updated));
            patchBooking(updated.bookingId, withBookingFields(updated));
          }}
        />
      )}

      {showFx && (
        <FxRatesDialog current={fxRates} onClose={() => setShowFx(false)}
          onSaved={fx => { setFxRates(fx); setShowFx(false); }} />
      )}

      {dialog && dialogBooking && (
        <OpportunityDialog
          bookingId={dialog.bookingId}
          bookingLabel={`${customerLabel(dialogBooking)} · ${formatDate(dialogBooking.startDate)}`}
          existing={dialog.existing}
          onClose={() => setDialog(null)}
          onSaved={link => { patchBooking(dialog.bookingId, withOpportunity(link)); setDialog(null); }}
        />
      )}
    </div>
  );
}
